import { FixedShape } from "@iventis/domain-model/model/fixedShape";
import { featureCollection, feature } from "@turf/helpers";
import GeoJSON from "geojson";
import { midpointHandleLayerId } from "../bridge/constants";
import { Listener, MoveEvent } from "../types/internal";
import { getMidpointsRhumb, getNearestHandleObject } from "./geojson-helpers";

export type Midpoint = GeoJSON.Feature<GeoJSON.Point, MidpointProperties>;

export enum MidpointHandleEvent {
    CLICK = "CLICK",
    DRAG = "DRAG",
    ENTER = "ENTER",
    LEAVE = "LEAVE",
}

interface ListenerPattern {
    [MidpointHandleEvent.CLICK]: ((point: Midpoint) => void)[];
    [MidpointHandleEvent.DRAG]: ((point: Midpoint) => void)[];
    [MidpointHandleEvent.ENTER]: ((point: GeoJSON.Point) => void)[];
    [MidpointHandleEvent.LEAVE]: (() => void)[];
}

export interface MidpointProperties {
    indexBefore: number;
    fixedShape?: FixedShape;
}

export interface Dragging {
    isDragging: boolean;
}

export class MidpointHandles {
    private listeners: ListenerPattern = {
        [MidpointHandleEvent.CLICK]: [],
        [MidpointHandleEvent.ENTER]: [],
        [MidpointHandleEvent.LEAVE]: [],
        [MidpointHandleEvent.DRAG]: [],
    };

    private geometry: GeoJSON.FeatureCollection<GeoJSON.Point>;

    private mouseDownHandleListener: Listener;

    /** The coordinates of the midpoint the pointer is over, if any */
    private hoveredHandle: GeoJSON.Position | undefined;

    private mouseMoveListener: Listener;

    private onContentUnderMouseChangedListener: Listener;

    constructor(
        coordinates: GeoJSON.Position[],
        public operations: {
            onMouseMove: (callback: (e: MoveEvent) => void, radius?: number) => Listener;
            onMouseDownHandle: (callback: (point: Midpoint) => void) => Listener;
            onContentUnderMouseChanged: (callback: (e: MoveEvent) => void) => Listener;
            setHandleGeometry: (geometry: GeoJSON.FeatureCollection<GeoJSON.Point>) => void;
        }
    ) {
        this.geometry = getCoordinateGeometry(coordinates);
        this.operations.setHandleGeometry(this.geometry);

        const contentUnderMouseChanged = (event) => {
            // Midpoints which are close together have overlapping hit areas, so pick the one nearest the pointer, as dragging does
            const midpointHandleObject = getNearestHandleObject(event.objects, midpointHandleLayerId, event) as GeoJSON.Feature<GeoJSON.Point> | undefined;

            if (midpointHandleObject === undefined) {
                if (this.hoveredHandle !== undefined) {
                    this.hoveredHandle = undefined;
                    this.listeners.LEAVE.forEach((listener) => listener());
                }
                return;
            }

            const [lng, lat] = midpointHandleObject.geometry.coordinates;
            if (this.hoveredHandle?.[0] === lng && this.hoveredHandle?.[1] === lat) {
                return;
            }

            // The pointer has gone straight from one midpoint onto another, so leave the first before entering the second
            if (this.hoveredHandle !== undefined) {
                this.listeners.LEAVE.forEach((listener) => listener());
            }
            this.hoveredHandle = [lng, lat];
            this.listeners.ENTER.forEach((listener) => listener(midpointHandleObject.geometry));
        };

        this.mouseMoveListener = this.operations.onMouseMove(contentUnderMouseChanged);

        this.onContentUnderMouseChangedListener = this.operations.onContentUnderMouseChanged(contentUnderMouseChanged);

        this.mouseDownHandleListener = this.operations.onMouseDownHandle((point) => {
            const moveAfterDown = this.operations.onMouseMove(() => {
                this.listeners[MidpointHandleEvent.DRAG].forEach((listener) => listener(point));
                moveAfterDown.remove();
                document.removeEventListener("mouseup", mouseUpFunction);
            });

            const mouseUpFunction = () => {
                this.listeners[MidpointHandleEvent.CLICK].forEach((listener) => listener(point));
                moveAfterDown.remove();
                document.removeEventListener("mouseup", mouseUpFunction);
            };

            document.addEventListener("mouseup", mouseUpFunction);
        });
    }

    removeHandles() {
        this.operations.setHandleGeometry({
            type: "FeatureCollection",
            features: [],
        });
        this.mouseDownHandleListener.remove();
    }

    destroy() {
        this.operations.setHandleGeometry({
            type: "FeatureCollection",
            features: [],
        });

        this.mouseDownHandleListener?.remove();
        this.mouseMoveListener.remove();
        this.onContentUnderMouseChangedListener.remove();
    }

    public on<E extends MidpointHandleEvent>(event: E, callback: ListenerPattern[keyof ListenerPattern][0]) {
        this.listeners[event].push(callback as () => void);
        return this;
    }
}

function getCoordinateGeometry(coordinates: GeoJSON.Position[]): GeoJSON.FeatureCollection<GeoJSON.Point> {
    if (coordinates === undefined || coordinates.length === 0) {
        return {
            type: "FeatureCollection",
            features: [],
        };
    }

    const midpoints = getMidpointsRhumb(coordinates);

    // Add the midpoint and the current index
    // If clicked, the midpoint will be added
    // at indexBefore + 1
    const midpointFeatures = midpoints.map(
        (point, index) =>
            feature(point, {
                indexBefore: index,
            }) as Midpoint
    );

    return featureCollection(midpointFeatures);
}
